import { useMutation } from '@tanstack/react-query';
import { Button, Form, Input, InputNumber, message, Modal, Popconfirm, Space } from 'antd';
import { DataTable } from 'common/data-table';
import bannerApiCall from 'apps/investcore/apiCalls/home-widget-banners';
import { useFetchApiList } from 'hooks/api';
import { useEffect, useState } from 'react';

const BannerFormModal = ({
  open,
  onClose,
  homeWidgetId,
  item,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  homeWidgetId: string;
  item?: any;
  onSaved: () => void;
}) => {
  const [form] = Form.useForm();
  const isEdit = !!item;

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue(
      item
        ? {
            title: item.title,
            description: item.description,
            external_link: item.external_link,
            background: item.background,
            thumbnail: item.thumbnail,
            screen_name: item.screen_name,
            screen_id: item.screen_id,
            type: item.type,
            ordering: item.ordering,
          }
        : { ordering: 0 }
    );
  }, [open, item]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: any) =>
      isEdit
        ? bannerApiCall.edit.queryFn({ id: item.id, payload })
        : bannerApiCall.create.queryFn({
            payload: { ...payload, home_widget_id: homeWidgetId },
          }),
    onSuccess: () => {
      message.success(isEdit ? 'Banner updated' : 'Banner created');
      form.resetFields();
      onSaved();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to save banner');
    },
  });

  return (
    <Modal
      title={isEdit ? 'Edit Banner' : 'Add Banner'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={values => mutate(values)}>
        <Form.Item name="title" label="Title">
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          name="background"
          label="Background Image URL"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="thumbnail" label="Thumbnail URL">
          <Input />
        </Form.Item>
        <Form.Item name="external_link" label="External Link">
          <Input />
        </Form.Item>
        <Form.Item name="screen_name" label="Screen Name">
          <Input />
        </Form.Item>
        <Form.Item name="screen_id" label="Screen ID">
          <Input />
        </Form.Item>
        <Form.Item name="type" label="Type">
          <Input placeholder="e.g. banner" />
        </Form.Item>
        <Form.Item name="ordering" label="Ordering">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Save
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const BannersSection = ({ homeWidgetId }: { homeWidgetId: string }) => {
  const {
    data: banners,
    load,
    refresh,
  } = useFetchApiList(bannerApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    if (homeWidgetId) {
      load({ home_widget_id: homeWidgetId });
    }
  }, [homeWidgetId]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<any>(undefined);

  const { mutate: deleteBanner, isPending: isDeleting } = useMutation({
    mutationFn: (params: { id: string }) => bannerApiCall.delete.queryFn(params),
    onSuccess: () => {
      message.success('Banner deleted');
      refresh();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete banner');
    },
  });

  const columns = [
    { accessorKey: 'title', header: 'Title' },
    { accessorKey: 'screen_name', header: 'Screen Name' },
    { accessorKey: 'external_link', header: 'External Link' },
    { accessorKey: 'ordering', header: 'Ordering' },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingBanner(row.original);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this banner?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteBanner({ id: row.original.id })}
          >
            <Button size="small" danger loading={isDeleting}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="pt-4 border-t mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-gray-800">Banners</h3>
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setEditingBanner(undefined);
            setModalOpen(true);
          }}
        >
          Add Banner
        </Button>
      </div>
      <DataTable noViewOptions data={banners || []} columns={columns} />
      <BannerFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        homeWidgetId={homeWidgetId}
        item={editingBanner}
        onSaved={refresh}
      />
    </div>
  );
};

export default BannersSection;
